/**
 * [INV-SPLIT 2026-10-07, user] "Split abc.com" — give one current-month website to two or more PMs.
 *
 * Pick a PM and type an amount per line; the amounts must add up to the row's amount before Save turns
 * on (a live line says how far off they are). Saving tells the server, which turns the row into one
 * row per PM and unmaps this month's retention on the website's payments (the user's rule — they are
 * mapped again, by PM, from the Payments tab). Admins only; the Inventory tab decides who sees it.
 * Look approved by the user from the live preview (artifact WywpMW9AW1g1GJ2ogiszgi), drawn with the
 * app's own glass dialog chrome so it sits beside ConfirmDialog without looking foreign.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { lgSurface, lgButtonPrimary, lgButtonSecondary, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { InventoryRow } from '../types';
import { splitInventoryRow } from '../services/api';
import { toastError } from './toastService';
import GlassSelect from './GlassSelect';
import { fluid } from '../styles/motion'; // [FLUID-3 2026-10-12]
import { usePresence } from '../hooks/usePresence'; // [FLUID-3 2026-10-12]
import { fluidDialog, fluidScrim, fluidCascade } from './Fluid'; // [FLUID-3 2026-10-12]

interface Props {
  /** The row to split; null = closed. */
  row: InventoryRow | null;
  /** PM names for the dropdowns (the same roster the PM Name cell offers). */
  pms: string[];
  /** [FLUID-3 2026-10-12] True while the Inventory page holds the popup mounted for its close (usePresence). */
  closing?: boolean;
  onClose: () => void;
  onDone: (result: { parts: InventoryRow[]; paymentsUnmapped: number }) => void;
}

interface Line { pm: string; amount: string }

const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const r2 = (n: number) => Math.round(n * 100) / 100;

function monthLabel(m: string): string {
  const [y, mo] = String(m || '').slice(0, 7).split('-').map(Number);
  if (!y || !mo) return '';
  return new Date(y, mo - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// [FLUID-3 2026-10-12, user] The popup opens and closes with the fluid motion (components/Fluid.tsx): the shade
// fades, the box rises 12px and grows from 0.95 on the spring and closes in 0.18 s, its title, lines and buttons
// rising in one after another. It had no motion at all before.
const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(10, 10, 10, 0.62)' : 'rgba(22, 25, 23, 0.42)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  ${(p) => fluidScrim(p.$open)}

  /* [PHONE-LAYOUT 2026-10-22] Phones: the popup comes to 8 px from the screen edges. */
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const Box = styled.div<{ $open: boolean }>`
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 3) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
  position: relative;
  width: 560px;
  max-width: 100%;
  max-height: calc(100vh / var(--app-zoom, 1) - 32px); /* [APP-FIT 2026-10-26] the window in page px */
  overflow-y: auto;
  border-radius: 20px;
  color: ${(p) => p.theme.text};
  background: ${(p) => p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(86, 86, 86, 0.42) 0%, rgba(54, 54, 54, 0.55) 100%)'
    : 'linear-gradient(180deg, rgba(255, 255, 255, 0.72) 0%, rgba(236, 239, 237, 0.82) 100%)'};
  backdrop-filter: blur(48px) saturate(180%);
  -webkit-backdrop-filter: blur(48px) saturate(180%);
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.65)'};
  box-shadow:
    0 30px 80px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0, 0, 0, 0.22)'},
    0 8px 24px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0, 0, 0, 0.12)'};

  /* [PHONE-LAYOUT 2026-10-22] Phones: never taller than the screen (it scrolls inside). */
  @media (max-width: 768px) {
    max-height: calc(100dvh - 24px);
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSurface(36)}
  border-color: transparent;
`;

const Head = styled.div`
  padding: 20px 24px 10px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  h3 { margin: 0; font-size: 17px; font-weight: 700; }
  span { font-size: 13px; color: ${(p) => p.theme.muted}; opacity: 0.8; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: 16 px sides, like the rest of the phone popups. */
  @media (max-width: 768px) {
    padding: 16px 16px 8px;
    h3 { overflow-wrap: anywhere; }
  }
`;

const Body = styled.div`
  padding: 4px 24px 18px;
  display: grid;
  gap: 12px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 16 px sides. */
  @media (max-width: 768px) {
    padding: 4px 16px 16px;
  }
`;

const LineGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px 36px;
  gap: 10px;
  align-items: center;
  @media (max-width: 520px) { grid-template-columns: minmax(0, 1fr) 120px 36px; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a narrower Amount box (it still fits "$ 12345.67") so the PM box shows
     "Choose a PM…" in full (it read "Choose a P…" at 390 px). */
  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr) 104px 36px;
    gap: 8px;
  }
`;

const Lbl = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  opacity: 0.75;
`;

const fieldCss = `
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  border-radius: 10px;
  padding: 0 12px;
  font: inherit;
  font-size: 13.5px;
`;

/* [INV-SPLIT 2026-10-07, user] The app's own glass dropdown, not a native <select>: the browser draws a
   native option list itself (white, with the dark theme's light text = unreadable — the user's screenshot).
   Sized like the Amount box beside it; the open list is GlassSelect's glass popover. */
const PmSelect = styled(GlassSelect)`
  && {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 38px;
    padding: 0 30px 0 12px;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 500;
    text-align: left;
    border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)'};
    background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)'};
    background-image: none;
    box-shadow: none;
    color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
  }
  && > span:first-child { text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &&:hover:not(:disabled) {
    background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.95)'};
  }
  &&[aria-expanded="true"],
  &&:focus-visible {
    outline: none;
    border-color: ${(p) => p.theme.green600};
    box-shadow: 0 0 0 3px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)'};
  }
`;

const AmountInput = styled.input`
  ${fieldCss}
  text-align: right;
  font-variant-numeric: tabular-nums;
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)'};
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)'};
  color: ${(p) => p.theme.text};
  &:focus { outline: none; border-color: ${(p) => p.theme.green600}; }
`;

const RemoveBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
  padding: 0;
  width: 36px;
  min-width: 36px;
`;

const AddLink = styled.button`
  justify-self: start;
  border: none;
  background: transparent;
  padding: 4px 2px;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => p.theme.greenInk};
  cursor: pointer;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid ${(p) => p.theme.green600}; outline-offset: 2px; border-radius: 4px; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 32 px tall tap target (was 24). */
  @media (max-width: 768px) {
    padding: 8px 2px;
  }
`;

const Check = styled.div<{ $ok: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$ok ? p.theme.greenInk : (p.theme.colorScheme === 'dark' ? '#f87171' : '#c81e1e'))};
  background: ${(p) => (p.$ok ? p.theme.greenTint : (p.theme.colorScheme === 'dark' ? 'rgba(248,113,113,0.12)' : 'rgba(200,30,30,0.08)'))};
`;

const Note = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${(p) => p.theme.muted};
  b { color: ${(p) => p.theme.text}; }
`;

const Foot = styled.div`
  padding: 12px 24px 18px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};

  /* [PHONE-LAYOUT 2026-10-22] Phones: 16 px sides. */
  @media (max-width: 768px) {
    padding: 12px 16px 16px;
  }
`;

const Primary = styled.button.attrs<LgPressProps>(LG_PRESS)`${lgButtonPrimary} min-width: 110px;
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */`;
const Secondary = styled.button.attrs<LgPressProps>(LG_PRESS)`${lgButtonSecondary} min-width: 92px;
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */`;

/** A fresh pair of lines for a row: its current PM first, then an empty line. */
const startLines = (row: InventoryRow | null): Line[] =>
  row ? [{ pm: String(row.pm ?? '').trim(), amount: '' }, { pm: '', amount: '' }] : [];

const SplitInventoryModal: React.FC<Props> = ({ row, pms, closing = false, onClose, onDone }) => {
  // Seeded on the FIRST render (the parent keys this component by row), so the popup never paints a
  // frame without its lines; the effect covers a caller that swaps the row without remounting.
  const [lines, setLines] = useState<Line[]>(() => startLines(row));
  const [busy, setBusy] = useState(false);
  // [FLUID-3 2026-10-12] Open one frame after mount (so the spring has a closed state to start from) until the
  // page starts the close.
  const open = usePresence(!closing, fluid.dialogOutMs).entered;

  // [FLUID-3 2026-10-12] ...and re-seeded when the same row is opened again while its last close is still
  // playing (the page keeps this instance then), so a new split always starts from fresh lines.
  useEffect(() => {
    if (!row || closing) return;
    setLines(startLines(row));
    setBusy(false);
  }, [row, closing]);

  // [FLUID-3 2026-10-12] Escape only while open: a closing popup is already on its way out.
  useEffect(() => {
    if (!row || closing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [row, busy, onClose, closing]);

  const total = r2(Number(row?.amount) || 0);
  const options = useMemo(() => {
    const set = new Set(pms.filter(Boolean));
    if (row?.pm) set.add(String(row.pm));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [pms, row]);

  const amounts = lines.map((l) => r2(parseFloat(l.amount) || 0));
  const sum = r2(amounts.reduce((s, a) => s + a, 0));
  const diff = r2(sum - total);
  const names = lines.map((l) => l.pm.trim().toLowerCase()).filter(Boolean);
  const duplicate = new Set(names).size !== names.length;
  const missingPm = lines.some((l) => !l.pm.trim());
  const zeroAmount = amounts.some((a) => !(a > 0));
  const addsUp = Math.abs(diff) < 0.005;
  const valid = lines.length >= 2 && addsUp && !duplicate && !missingPm && !zeroAmount;

  let problem = '';
  if (duplicate) problem = 'Each PM can be chosen only once.';
  else if (missingPm) problem = 'Choose a PM on every line.';
  else if (zeroAmount && sum > 0) problem = 'Every PM needs an amount above $0.00.';

  const setLine = (i: number, patch: Partial<Line>) =>
    setLines((prev) => prev.map((l, k) => (k === i ? { ...l, ...patch } : l)));

  const save = async () => {
    if (!row || !valid || busy) return;
    setBusy(true);
    try {
      const result = await splitInventoryRow(row.id, lines.map((l, k) => ({ pm: l.pm.trim(), amount: amounts[k] })));
      onDone(result);
    } catch (err: any) {
      toastError(err?.response?.data?.error || 'Could not split the row');
      setBusy(false);
    }
  };

  if (!row) return null;
  // [FLUID-3 2026-10-12] Inert while it closes: nothing in it may be used during the 0.18 s exit.
  return createPortal(
    <Overlay onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }} $open={open} inert={closing}>
      <Box role="dialog" aria-modal="true" aria-label={`Split ${row.website}`} $open={open}>
        <Head>
          <h3>Split {row.website}</h3>
          <span>{monthLabel(row.month)} · {money(total)}</span>
        </Head>
        <Body>
          <LineGrid><Lbl>PM</Lbl><Lbl>Amount</Lbl><span /></LineGrid>
          {lines.map((l, i) => (
            <LineGrid key={i}>
              <PmSelect
                title={`PM ${i + 1}`}
                value={l.pm}
                onChange={(e) => setLine(i, { pm: e.target.value })}
                disabled={busy}
              >
                <option value="">Choose a PM…</option>
                {options.map((p) => <option key={p} value={p}>{p}</option>)}
              </PmSelect>
              <AmountInput
                id={`split-amount-${i}`}
                aria-label={`Amount for PM ${i + 1}`}
                inputMode="decimal"
                placeholder="$ 0.00"
                value={l.amount === '' ? '' : `$ ${l.amount}`}
                onChange={(e) => {
                  let raw = e.target.value.replace(/[^0-9.]/g, '');
                  const dot = raw.indexOf('.');
                  if (dot !== -1) raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, '').slice(0, 2);
                  setLine(i, { amount: raw });
                }}
                disabled={busy}
              />
              <RemoveBtn
                type="button"
                aria-label={`Remove PM ${i + 1}`}
                title={lines.length <= 2 ? 'A split needs at least two PMs' : 'Remove this PM'}
                disabled={busy || lines.length <= 2}
                onClick={() => setLines((prev) => prev.filter((_, k) => k !== i))}
              >
                <i className="fas fa-times" />
              </RemoveBtn>
            </LineGrid>
          ))}
          <AddLink type="button" disabled={busy} onClick={() => setLines((prev) => [...prev, { pm: '', amount: '' }])}>
            + Add another PM
          </AddLink>
          <Check $ok={addsUp} role="status">
            <span>
              {addsUp
                ? `Adds up to ${money(total)}`
                : diff > 0
                  ? `${money(diff)} over the ${money(total)} amount`
                  : `${money(-diff)} short of the ${money(total)} amount`}
            </span>
            <span aria-hidden="true">{addsUp ? '✓' : '✕'}</span>
          </Check>
          {problem && <Note style={{ color: 'inherit' }}>{problem}</Note>}
          <Note>
            This month&apos;s payments for <b>{row.website}</b> that already have retention will be <b>unmapped</b>,
            so you can map them again by PM.
          </Note>
        </Body>
        <Foot>
          <Secondary type="button" onClick={onClose} disabled={busy}>Cancel</Secondary>
          <Primary type="button" onClick={save} disabled={!valid || busy}>{busy ? 'Saving…' : 'Save split'}</Primary>
        </Foot>
      </Box>
    </Overlay>,
    document.body,
  );
};

export default SplitInventoryModal;
